import React, { useEffect, useState } from 'react'
import { Document, Page, View, Text, StyleSheet, PDFDownloadLink, Font } from '@react-pdf/renderer'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material'
import { DownloadOutlined } from '@mui/icons-material'
import { stitchShifts, getTotalShiftHours, timezoneOffsets } from '../../Utilities/shiftUtils'
import PdfEmploymentTable from './PdfEmploymentTable'
import yu from '../../fonts/yumin.ttf'
import PdfSalesTable from './PdfSalesTable'
import moment from 'moment'
import PdfFooter from './PdfFooter'
import { buildPdfHourlyPreds, getOpsHoursAndStoreHours, getPdfDisplayHour } from '../../Utilities/pdfExportUtils'

// Preload font as data URL to bypass @react-pdf's 5-second timeout
// This fetches the font with a longer timeout and converts it to base64
let fontDataUrl = null
let fontLoadingPromise = null

const loadFontAsDataUrl = async (fontUrl, timeoutMs = 30000) => {
    return new Promise((resolve, reject) => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

        fetch(fontUrl, { signal: controller.signal })
            .then(response => {
                clearTimeout(timeoutId)
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                return response.blob()
            })
            .then(blob => {
                const reader = new FileReader()
                reader.onloadend = () => {
                    const base64data = reader.result
                    resolve(base64data)
                }
                reader.onerror = reject
                reader.readAsDataURL(blob)
            })
            .catch(error => {
                clearTimeout(timeoutId)
                reject(error)
            })
    })
}

// Preload font on module load
if (!fontLoadingPromise) {
    fontLoadingPromise = loadFontAsDataUrl(yu, 30000) // 30 second timeout
        .then(dataUrl => {
            fontDataUrl = dataUrl
            return dataUrl
        })
        .catch(error => {
            console.warn('Failed to preload font as data URL:', error)
            // Fallback to original URL
            fontDataUrl = yu
        })
}

export const priceFormatter = price => {
    return price ? String(Math.round(price)).slice(0, -3) : '-'
}

const PdfExportButton = ({
    shiftDate,
    employments,
    shifts,
    predictedSales,
    store,
    staffRequirements,
    storeHour,
    dailyNotes,
    missingStaffHours,
    hoursWithStaffReq,
    totalActualStaff
}) => {
    const [dialogue, setDialogue] = useState(false)
    const [hourlyPleds, setHourlyPreds] = useState([])
    const [stitchedShifts, setStichedShifts] = useState([])
    const [storeHours, setStoreHours] = useState([])
    const [opsHours, setOpsHours] = useState([])
    const [staffPerHour, setStaffPerHour] = useState({})
    const [employmentShifts, setEmploymentShifts] = useState([])
    const [pageBreak, setPageBreak] = useState(false)
    const [hourlyEmpSalesPred, setHourlyEmpSalesPred] = useState()
    const [startHour, setStartHour] = useState()
    const [endHour, setEndHour] = useState()
    const [hours, setHours] = useState()
    const [leftColWidth, setLeftColWidth] = useState()

    useEffect(() => {
        if (opsHours.length > 0) {
            const start = opsHours[0] || 7
            const end = opsHours[1] || start + 21
            const hours = Array.from(Array(end - start), (x, i) => i + start)
            // we need 21 hours columns in order to fit the table, 1 column is 19.0px
            const missingWidth = (21 - (hours[hours.length - 1] - hours[0] + 1)) * 19.0
            const leftColWidth = 130.0 + missingWidth + 'px'

            setStartHour(start)
            setEndHour(end)
            setHours(hours)
            setLeftColWidth(leftColWidth)
        }
    }, [opsHours])

    useEffect(() => {
        if (employments.length > 0 && stitchedShifts) {
            let empShifts = employments
                .map(employment => {
                    const shifts = stitchedShifts?.filter(shift => {
                        return shift?.employment_id === employment?.id
                    })
                    const totalShiftHours = getTotalShiftHours(shifts)
                    return {
                        ...employment,
                        shifts,
                        totalShiftHours: totalShiftHours || '公'
                    }
                })
                .filter(employment => employment.shifts.length > 0)
            const emptyRows = []
            for (let i = 0; i < 2; i++) {
                emptyRows.push({
                    key: `empty-${i}`,
                    user: {
                        name_first: ''
                    },
                    shifts: []
                })
            }
            setEmploymentShifts([...empShifts, ...emptyRows])
            setPageBreak(empShifts.length >= 30)
        }
    }, [employments, stitchedShifts])

    useEffect(() => {
        if (storeHour && storeHour.open_time && storeHour.close_time) {
            getOpsHoursAndStoreHours(storeHour, setOpsHours, setStoreHours)
        }
    }, [storeHour])
    useEffect(() => {
        if (store?.id && shifts) {
            setStichedShifts(stitchShifts(shifts, timezoneOffsets[store?.time_zone], true))

            const staffPerHour = {}
            shifts.forEach(shift => {
                const displayHour = getPdfDisplayHour(shiftDate, shift.startDate)
                staffPerHour[displayHour] = staffPerHour[displayHour] ? staffPerHour[displayHour] + 0.25 : 0.25
            })
            setStaffPerHour(staffPerHour)
        }
    }, [store, shifts, shiftDate])
    useEffect(() => {
        if (staffRequirements.length > 0) {
            const customerPredictedSales =
                predictedSales?.customer_predicted_sales ??
                (typeof predictedSales === 'number' ? predictedSales : null)
            setHourlyPreds(
                buildPdfHourlyPreds(staffRequirements, shiftDate, missingStaffHours, customerPredictedSales)
            )
        }
    }, [staffRequirements, missingStaffHours, shiftDate, predictedSales])
    useEffect(() => {
        if (predictedSales.length > 0 && Object.keys(hoursWithStaffReq).length > 0) {
            setHourlyEmpSalesPred(Math.round(predictedSales[0] / hoursWithStaffReq?.fullCount).toLocaleString('ja-JP'))
        }
    }, [predictedSales, hoursWithStaffReq])
    const doc = (
        <Document>
            <Page style={styles.body}>
                <View style={styles.header}>
                    <Text style={styles.headerText}>ワークスケジュール表</Text>
                </View>
                <View style={{ ...styles.header, marginTop: 0 }}>
                    <Text style={{ fontSize: '12px' }}>
                        {moment(shiftDate, 'YYYY-MM-DD').format('YYYY年 M月D日 ddd')}{' '}
                        {storeHours.length > 0 ? `営業時間：${storeHours[0]}〜${storeHours[1]}` : ''}
                    </Text>
                </View>
                <PdfSalesTable predictedSales={predictedSales} store={store} dailyNotes={dailyNotes} />
                <PdfEmploymentTable
                    employmentShifts={employmentShifts}
                    shiftDate={shiftDate}
                    startHour={startHour}
                    endHour={endHour}
                    leftColWidth={leftColWidth}
                    HOURS={hours}
                />
                <PdfFooter
                    preds={hourlyPleds}
                    predictedSales={predictedSales}
                    nextPage={pageBreak}
                    hoursWithStaffReq={hoursWithStaffReq}
                    hourlyEmpSalesPred={hourlyEmpSalesPred}
                    leftColWidth={leftColWidth}
                    HOURS={hours}
                    staffPerHour={staffPerHour}
                    totalActualStaff={totalActualStaff}
                />
            </Page>
        </Document>
    )

    return (
        <>
            <IconButton aria-label="download schedule" onClick={() => setDialogue(true)}>
                <DownloadOutlined />
            </IconButton>
            <Dialog open={dialogue}>
                <DialogTitle>{'シフト表の出力'}</DialogTitle>
                <DialogContent>{'シフト表をPDF形式で出力します。よろしいですか？'}</DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={() => setDialogue(false)}>
                        {'キャンセル'}
                    </Button>
                    <PDFDownloadLink document={doc} fileName={`ワークスケジュール表-${store?.name}-${shiftDate}.pdf`}>
                        {({ loading, error }) => {
                            if (error) {
                                return 'エラーが発生しました。'
                            }
                            return loading ? (
                                ''
                            ) : (
                                <Button
                                    onClick={() => {
                                        setDialogue(false)
                                    }}
                                    autoFocus
                                >
                                    {'出力する'}
                                </Button>
                            )
                        }}
                    </PDFDownloadLink>
                </DialogActions>
            </Dialog>

            {/* Hidden PDFDownloadLink that triggers the font download. This is needed so that the font downloads
                when the button is rendered and does not wait for the button to be clicked. */}
            <div style={{ display: 'none' }}>
                <PDFDownloadLink document={<DummyDocument />} fileName="dummy.pdf">
                    {({ loading }) => (loading ? 'Loading document...' : 'Download now!')}
                </PDFDownloadLink>
            </div>
        </>
    )
}

// Dummy document that uses the font
const DummyDocument = () => (
    <Document>
        <Page>
            <Text style={{ fontFamily: 'Yu Mincho' }}>Dummy</Text>
        </Page>
    </Document>
)

const styles = StyleSheet.create({
    body: {
        padding: 10,
        fontFamily: 'Yu Mincho'
    },
    header: {
        display: 'flex',
        justifyContent: 'center',
        margin: '7.5px auto 0px'
    },
    headerText: {
        fontSize: '14px',
        fontWeight: 'bold'
    }
})

// Register font with preloaded data URL to bypass timeout
// Wait for font to be loaded before registering
fontLoadingPromise.then(() => {
    Font.register({
        family: 'Yu Mincho',
        src: fontDataUrl || yu
    })
}).catch(() => {
    // Fallback to original URL if preload failed
    Font.register({
        family: 'Yu Mincho',
        src: yu
    })
})

export default PdfExportButton
